import type { LucideIcon } from "lucide-react";
import {
  Award,
  FileText,
  Lock,
  MapPin,
  Shield,
  ShieldCheck,
} from "lucide-react";

export type CredentialStatus = "earned" | "in_progress";

export interface CredentialDef {
  key: string;
  label: string;
  tier: 1 | 2 | 3 | 4;
  icon: LucideIcon;
  blurb: string;
  /** Lowercase keywords used to auto-suggest claiming this badge when a gap matches. */
  matchKeywords: string[];
}

export const CREDENTIAL_CATALOG: CredentialDef[] = [
  {
    key: "ein",
    label: "EIN",
    tier: 1,
    icon: FileText,
    blurb: "IRS Employer Identification Number",
    matchKeywords: ["ein", "employer identification"],
  },
  {
    key: "sam_gov",
    label: "SAM.gov / UEI",
    tier: 1,
    icon: ShieldCheck,
    blurb: "Required for federal funding",
    matchKeywords: ["sam.gov", "sam ", "uei", "unique entity"],
  },
  {
    key: "gen_liab",
    label: "General Liability",
    tier: 1,
    icon: Shield,
    blurb: "$1M / $2M coverage",
    matchKeywords: ["general liability", "liability insurance"],
  },
  {
    key: "dbe",
    label: "DBE Certified",
    tier: 2,
    icon: Award,
    blurb: "Alabama UCP / ALDOT Disadvantaged Business Enterprise",
    matchKeywords: ["dbe", "disadvantaged business"],
  },
  {
    key: "mbe",
    label: "MBE (NMSDC)",
    tier: 2,
    icon: Award,
    blurb: "Minority Business Enterprise via NMSDC",
    matchKeywords: ["mbe", "minority business", "nmsdc"],
  },
  {
    key: "8a",
    label: "8(a) Certified",
    tier: 2,
    icon: Award,
    blurb: "SBA 8(a) Business Development",
    matchKeywords: ["8(a)", "8a ", "sba 8"],
  },
  {
    key: "hubzone",
    label: "HUBZone",
    tier: 2,
    icon: MapPin,
    blurb: "SBA HUBZone certified",
    matchKeywords: ["hubzone", "hub zone"],
  },
  {
    key: "wosb",
    label: "WOSB",
    tier: 2,
    icon: Award,
    blurb: "Women-Owned Small Business",
    matchKeywords: ["wosb", "women-owned", "women owned"],
  },
  {
    key: "soc2_type1",
    label: "SOC 2 Type I",
    tier: 3,
    icon: Lock,
    blurb: "Enterprise data trust baseline",
    matchKeywords: ["soc 2", "soc2"],
  },
];

export const credentialByKey = (key: string): CredentialDef | undefined =>
  CREDENTIAL_CATALOG.find((c) => c.key === key);

/** Find a credential whose keywords appear in the given text. */
export const matchCredentialFromText = (text: string): CredentialDef | undefined => {
  const t = text.toLowerCase();
  return CREDENTIAL_CATALOG.find((c) => c.matchKeywords.some((kw) => t.includes(kw)));
};

/** Stable normalized hash key for an assessment item (action/gap). Async because uses subtle crypto. */
export const hashItemKey = async (type: "action" | "gap", title: string): Promise<string> => {
  const normalized = `${type}|${title.trim().toLowerCase()}`;
  const buf = new TextEncoder().encode(normalized);
  const digest = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
    .slice(0, 32);
};